import apiService from './api';

export interface UserPreferences {
  tableDensity?: 'comfortable' | 'compact';
  visibleColumns?: string[];
  columnOrder?: string[];
  columnWidths?: Record<string, number>;
  pageSize?: number;
  favoriteUserIds?: string[];
}

// Default preferences
export const DEFAULT_PREFERENCES: UserPreferences = {
  tableDensity: 'compact',
  visibleColumns: ['checkbox', 'date', 'description', 'amount', 'status', 'assignedTo'],
  columnOrder: ['checkbox', 'date', 'description', 'amount', 'checkNumber', 'type', 'cmdAmount', 'lisAmount', 'missingEobAmount', 'priorMonthErrorAmount', 'returnedCheck', 'creditCardOffMonth', 'miscCorrection', 'status', 'assignedTo', 'overrideDate'],
  columnWidths: {},
  pageSize: 50,
  favoriteUserIds: []
};

class PreferencesService {
  private cache: UserPreferences = { ...DEFAULT_PREFERENCES };
  private cacheLoaded = false;
  private saveTimeout: NodeJS.Timeout | null = null;

  /**
   * Get user preferences from API (with caching)
   */
  async getPreferences(): Promise<UserPreferences> {
    // Return cache if already loaded
    if (this.cacheLoaded) {
      return this.cache;
    }

    try {
      const response = await apiService.get<UserPreferences>('/auth/preferences');
      const data = (response.data as any)?.data || {};

      // Merge with defaults to ensure all keys exist
      this.cache = { ...DEFAULT_PREFERENCES, ...data };
      this.cacheLoaded = true;
      return this.cache;
    } catch (error) {
      console.warn('Failed to fetch preferences, using defaults:', error);
      return DEFAULT_PREFERENCES;
    }
  }

  /**
   * Save preferences to API (debounced to avoid excessive API calls)
   */
  async savePreferences(preferences: Partial<UserPreferences>, immediate = false): Promise<void> {
    // Update cache immediately for responsive UI
    this.cache = { ...this.cache, ...preferences };

    // Clear existing timeout
    if (this.saveTimeout) {
      clearTimeout(this.saveTimeout);
      this.saveTimeout = null;
    }

    // Debug: Log what's being saved
    console.log('[PREFERENCES DEBUG] Attempting to save:', JSON.stringify(preferences));

    // If immediate save requested, save now
    if (immediate) {
      try {
        await apiService.patch('/auth/preferences', preferences);
        console.log('[PREFERENCES] Saved immediately to server');
      } catch (error: any) {
        console.error('[PREFERENCES] Failed to save:', error);
        console.error('[PREFERENCES] Payload was:', JSON.stringify(preferences));
        console.error('[PREFERENCES] Server response:', error.response?.data);
        throw error;
      }
      return;
    }

    // Otherwise debounce (reduced to 200ms for better UX)
    this.saveTimeout = setTimeout(async () => {
      try {
        await apiService.patch('/auth/preferences', preferences);
        console.log('[PREFERENCES] Saved to server (debounced)');
      } catch (error: any) {
        console.error('[PREFERENCES] Failed to save:', error);
        console.error('[PREFERENCES] Payload was:', JSON.stringify(preferences));
        console.error('[PREFERENCES] Server response:', error.response?.data);
        // Don't throw - preferences are best-effort
      }
      this.saveTimeout = null;
    }, 200); // Reduced from 500ms to 200ms
  }

  /**
   * Restore cache state after an optimistic update fails.
   */
  restoreCachedPreferences(preferences: Partial<UserPreferences>): void {
    this.cache = { ...this.cache, ...preferences };
  }

  /**
   * Flush any pending saves immediately (call before navigation/unmount)
   */
  async flushPendingSaves(): Promise<void> {
    if (this.saveTimeout) {
      clearTimeout(this.saveTimeout);
      this.saveTimeout = null;

      try {
        // Save current cache state
        await apiService.patch('/auth/preferences', this.cache);
        console.log('[PREFERENCES] Flushed pending saves');
      } catch (error) {
        console.error('[PREFERENCES] Failed to flush:', error);
      }
    }
  }

  /**
   * Clear cache (call on logout)
   */
  async clearCache(): Promise<void> {
    // Flush any pending saves before clearing
    await this.flushPendingSaves();

    this.cache = { ...DEFAULT_PREFERENCES };
    this.cacheLoaded = false;
  }
}

export const preferencesService = new PreferencesService();
export default preferencesService;
